import React, { useEffect, useState } from "react";
import {
  LoadingSpinner,
  MeatballMenu,
  MeatballMenuItem,
  Severity
} from "@velaro/velaro-component-lib";
import { SortableTable } from "@velaro/velaro-component-lib";
import AIConfigurationTablePopover from "./AIConfigurationTablePopover";
import useAzureIndexes from "@/hooks/useAzureIndexes";
import NewIndexLanding from "./NewIndexLanding";
import useSubscriptions from "@/hooks/useSubscriptions";
import DeleteIndexModal from "./DeleteIndexModal";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import EditIndexModal from "./EditIndexModal";
import { AzureIndex } from "../types";
import AIChatbotSettingsTrainingTabFile from "./AIChatbotFileTrainingIndex";
import IconEditPencil from "@/icons/IconEditPencil";
import IconDeactivateUserPerson from "@/icons/IconDeactivateUserPerson";

export default function AIIndexes() {
  const { azureIndexes, loading, remove, update } = useAzureIndexes();
  const [tableData, setTableData] = useState(azureIndexes);
  const { subscription } = useSubscriptions();
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentIndexId, setCurrentIndexId] = useState(0);
  const api = useApi();
  const toastAlert = useToastAlert();

  useEffect(() => {
    setTableData(azureIndexes);
  }, [azureIndexes]);

  function handleEdit(id: number) {
    console.log("Edit");
    setCurrentIndexId(id);
    setShowEditModal(true);
  }

  function handleDelete(id: number) {
    console.log("Delete");
    setCurrentIndexId(id);
    setShowDeleteModal(true);
  }

  async function deleteIndex(id: number) {
    const response = await api.messaging.delete(
      `AzureIndexes/DeleteIndex/${id}`
    );
    const result = await response.json();
    if (response.status !== 200) {
      toastAlert.displayToast(Severity.Error, result);
      return;
    }
    remove(id);
    setShowDeleteModal(false);
    toastAlert.displayToast(Severity.Success, "Index deleted successfully");
  }

  async function editIndex(index: AzureIndex) {
    const response = await api.messaging.post("AzureIndexes/EditIndex", {
      id: index.id,
      name: index.name,
      searchSize: index.searchSize,
      semanticSearchEnabled: index.semanticSearchEnabled,
      type: index.type
    });
    const result = await response.json();
    if (response.status !== 200) {
      toastAlert.displayToast(Severity.Error, result);
      return;
    }
    update(index);
    setShowEditModal(false);
    toastAlert.displayToast(Severity.Success, "Index updated successfully");
  }

  function getMeatballItems(id: number): MeatballMenuItem[] {
    return [
      {
        icon: IconEditPencil,
        label: "Edit Index",
        onClick: () => handleEdit(id)
      },
      {
        icon: IconDeactivateUserPerson,
        label: "Delete Index",
        onClick: () => handleDelete(id)
      }
    ];
  }

  //Todo translate
  const columns = [
    { label: "Name", accessor: "name" },
    { label: "Type", accessor: "type" },
    { label: "Search Size", accessor: "searchSize" },
    { label: "AI Enhanced", accessor: "semanticSearchEnabled" },
    { label: "", accessor: "edit" }
  ];

  if (loading) return <LoadingSpinner />;

  //Todo translate
  if (azureIndexes.length == 0)
    return (
      <>
        <NewIndexLanding />
        <AIChatbotSettingsTrainingTabFile />
      </>
    );

  return (
    <>
      <div className="mb-4 text-slate-gray-500">
        Trainings: {azureIndexes.length} / {subscription?.maxIndexCount}
      </div>
      <div className="bg-white border-[1px] rounded-2xl shadow-sm">
        <table className="bg-white rounded-2xl text-left w-full">
          <thead className="border-b-[1px]">
            <tr>
              {columns.map(({ label, accessor }) => {
                return (
                  <th className="p-4 items-center" key={accessor}>
                    <div className="flex items-center">{label}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {tableData.map((data: Record<string, any>, index: number) => {
              return (
                <tr key={index} className={`hover:bg-slate-100`}>
                  {columns.map(({ accessor }) => {
                    if (accessor === "edit") {
                      return (
                        <div className="py-4" key={data.id}>
                          <MeatballMenu
                            menuItems={getMeatballItems(data.id)}
                            key={accessor}
                          />
                        </div>
                      );
                    }
                    return (
                      <td className="p-4" key={accessor}>
                        {data[accessor] ?? "——"}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <AIChatbotSettingsTrainingTabFile />
      {showDeleteModal && (
        <DeleteIndexModal
          id={currentIndexId}
          clearDeleteModal={() => setShowDeleteModal(false)}
          deleteIndex={deleteIndex}
        />
      )}
      {showEditModal && (
        <EditIndexModal
          tempSearchIndex={
            azureIndexes.find((index) => index.id === currentIndexId)!
          }
          clearSearchModal={() => setShowEditModal(false)}
          editIndex={editIndex}
        />
      )}
    </>
  );
}
